// hooks/useEffectiveUser.ts
import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useChameleon } from '@/contexts/ChameleonContext';
import type { AuthUser } from '@/types/auth';

/**
 * Returns the auth user as the *worn* identity when chameleon mode is active.
 * Components that gate UI on isSuperUser, isBroadcaster, entity_id etc. should use
 * this instead of reading useAuth().authState.user directly, so the frontend shows
 * what the backend will actually enforce from the X-Impersonate-* headers.
 *
 * TWO EXCEPTIONS, and they matter:
 *
 * 1. The chameleon's own controls (`ChameleonPicker`, `DemoToggle`) must keep reading
 *    the REAL user. Gate them on this and a chameleoned superuser is no longer a
 *    superuser, the control unmounts, and there is no way out of chameleon mode short
 *    of clearing site data. Likewise `BookingsTab`'s distributor check, which is
 *    deliberately "real distributor AND not currently wearing anyone".
 *
 * 2. Territory is NOT overridden — country_code, currency_code, operationalCountries
 *    and isMultiCountry still describe the real user, because the chameleon session
 *    only carries the worn entity's id, type and name. Guessing the worn entity's
 *    territory client-side would drift from what the backend enforces; the fix is for
 *    the session endpoint to return the effective identity (backend note filed).
 *    Until then, treat territory-derived UI as known-wrong under chameleon.
 */
export const useEffectiveUser = (): AuthUser | null => {
    const { authState } = useAuth();
    const { chameleon } = useChameleon();
    const user = authState.user;

    return useMemo(() => {
        if (!user || !chameleon.active || !chameleon.entityType || chameleon.entityId == null) return user;

        const entityType = chameleon.entityType.toUpperCase() as AuthUser['entityType'];

        return {
            ...user,
            role: 'ROLE_USER',
            name: chameleon.entityName || user.name,
            isSuperUser: false,
            isBroadcaster: entityType === 'BROADCASTER',
            isAgency: entityType === 'AGENCY',
            isCustomer: entityType === 'CUSTOMER',
            isAdTechUser: entityType === 'ADTECH',
            // Must be overridden too: a distributor wearing an agency is an agency.
            // Left set, it hands the worn agency a DOR selector and an agency picker
            // that belong to Broadcast Map, not to the entity being worn.
            isDistributor: entityType === 'DISTRIBUTOR',
            isPartitioned: false,
            partition_id: null,
            // The worn entity's id — the chameleon session knows this, so there is no
            // excuse for leaving the real one in place. Anything keyed on entity_id
            // (booking publisher_id, "my channels", adtech requirements) resolves
            // against the wrong entity otherwise, and silently writes it into payloads.
            entity_id: chameleon.entityId,
            entityType,
            entity_type: entityType,
        };
    }, [user, chameleon.active, chameleon.entityType, chameleon.entityId, chameleon.entityName]);
};
